import { AnimatePresence, motion } from 'framer-motion';
import Link, { LinkProps } from 'next/link';
import { useMemo } from 'react';
import styled from 'styled-components';
import { iconCheckboxVariant } from '@/styles/motion';
import Flex, { Props as FlexProps } from '../Flex';
import { IconCheckbox } from '../icons';

export interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  inline?: FlexProps['$inline'];
  direction?: FlexProps['$direction'];
  justify?: FlexProps['$justify'];
  align?: FlexProps['$align'];
  wrap?: FlexProps['$wrap'];
  gap?: FlexProps['$gap'];
  grow?: FlexProps['$grow'];
  width?: FlexProps['$width'];
  height?: FlexProps['$height'];
  minWidth?: FlexProps['$minWidth'];
  minHeight?: FlexProps['$minHeight'];
  maxWidth?: FlexProps['$maxWidth'];
  maxHeight?: FlexProps['$maxHeight'];
  margin?: FlexProps['$margin'];
  padding?: FlexProps['$padding'];
  label?: React.ReactNode;
}

const CheckboxLabelLinkRoot = styled.span`
  position: relative;
`;
const CheckboxLabelLinkArea = styled(Link)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

export function CheckboxLabelLink({
  children,
  ...props
}: React.PropsWithChildren & LinkProps) {
  return (
    <CheckboxLabelLinkRoot>
      {children}
      <CheckboxLabelLinkArea {...props} />
    </CheckboxLabelLinkRoot>
  );
}

const Root = styled(Flex)`
  position: relative;
  ${({ theme }) => theme.transition.default}

  &:active {
    opacity: 0.8;
  }
`;
const IconContainer = styled.div`
  position: relative;
`;
const Input = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

export default function Checkbox({
  value,
  inline,
  direction,
  justify = 'center',
  align = 'center',
  wrap,
  gap = 'xxxs',
  grow,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  margin,
  padding,
  label,
  ...props
}: Props) {
  // memo
  const RootProps = useMemo(
    () => ({
      $inline: inline,
      $direction: direction,
      $justify: justify,
      $align: align,
      $wrap: wrap,
      $gap: gap,
      $grow: grow,
      $width: width,
      $height: height,
      $minWidth: minWidth,
      $minHeight: minHeight,
      $maxWidth: maxWidth,
      $maxHeight: maxHeight,
      $margin: margin,
      $padding: padding,
    }),
    [
      inline,
      direction,
      justify,
      align,
      wrap,
      gap,
      grow,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      margin,
      padding,
    ]
  );

  return (
    <Root {...RootProps}>
      <IconContainer>
        <AnimatePresence>
          {value && (
            <motion.div
              initial="incoming"
              animate="active"
              exit="exit"
              key="iconCheckbox"
              variants={iconCheckboxVariant}
            >
              <IconCheckbox size={16} color="active" />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!value && (
            <motion.div
              initial="incoming"
              animate="active"
              exit="exit"
              key="iconCheckbox"
              variants={iconCheckboxVariant}
            >
              <IconCheckbox size={16} color="secondary" />
            </motion.div>
          )}
        </AnimatePresence>
      </IconContainer>
      <Input {...props} value={value} />
      {label}
    </Root>
  );
}
